import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import switchFunc from 'lib/function/switchFunc'
import { getDismissedBannerIds, recordSiteBannerDismissal } from 'storage/siteBanner'
import NotificationBannerModal from './NotificationBannerModal'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isBannerMessagePayload } from 'lib/lab/dynamicPayload'
import moment from 'moment'
import { useIsBannerHidden } from 'hooks/useIsBannerHidden'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchLabHeaderBanners } from 'actions/SystemActions'
import { useLabRouteRequestContext } from 'hooks/useLabRouteRequestContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { getHeaderBannerPresentation, getRenderableHeaderBanners, getTakeoverBannerLocation, type LuxPlusRegionOverride } from './NotificationBannerV3.utils'
import { getImageUrl, getSimpleImageUrl } from 'lib/image/imageUtils'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { renderHeaderBannerRichText } from './headerBannerRichText'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'
import styled from 'styled-components'
import { rem } from 'polished'
import { getIsLuxPlusMemberOutsideRegion } from 'luxPlus/selectors/featureToggle'
import useSwitchRegion from 'hooks/useSwitchRegion'
import { BANNER_BUTTON_PRIMARY_CONTRAST_COLOR_VAR, BANNER_BUTTON_TEXT_COLOR_VAR } from 'LuxKit/Banners/bannerButtonColorStyles'
import config from 'constants/config'

// Header banner ids are per-environment (staging and production have separate lab configs), so
// the inline-CTA banner is identified by env + its numeric id in that env.
const INLINE_CTA_HEADER_BANNER_ID = config.APP_ENV === 'production' ? 34 : 199

const getBannerKind = switchFunc<PageBannerCarouselItem['kind'], App.LabBannerMessagePayload['bannerType']>({
  warning: 'warning',
  error: 'critical',
  success: 'promotional-primary',
}, 'informational-primary')

const getBannerIcon = switchFunc<React.ReactElement | undefined, App.LabBannerMessagePayload['bannerType']>({
  warning: <LineExclamationTriangleIcon />,
  error: <LineExclamationTriangleIcon />,
  success: <LineCheckCircleIcon />,
}, <LineInfoCircleIcon />)

const BANNER_DISMISSAL_PERIOD_DAYS = 7
const DEFAULT_HEADER_BANNER_BACKGROUND = '#CAFFEB'

const HeaderBannerLeadingIcon = styled.img`
  height: ${rem(44)};
  width: auto;
  max-width: 132px;
  object-fit: contain;
  display: block;
`

const HeaderBannerImageBackground = styled.div<{ $imageUrl: string }>`
  position: absolute;
  inset: 0;
  background-color: ${DEFAULT_HEADER_BANNER_BACKGROUND};
  background-image: url(${({ $imageUrl }) => $imageUrl});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`

function useNotificationBannerItems(): Array<PageBannerCarouselItem> {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const campaign = useLabFeatureFlag(LabFeatureFlags.enableTopBannerMessage)
  const headerBanners = useAppSelector(state => state.system.features.headerBanners)
  const headerBannersRequestPath = useAppSelector(state => state.system.features.headerBannersRequestPath)
  const headerBannersLoadedPath = useAppSelector(state => state.system.features.headerBannersLoadedPath)
  const isHidden = useIsBannerHidden('notificationBannerV3')
  const isMobile = useScreenSizeOnly('mobile')
  const isSearchPage = useIsSearchPage()
  const isOfferPage = useIsOfferPage()
  const takeoverBannerLocation = getTakeoverBannerLocation(isSearchPage, isOfferPage)
  const shouldShowTakeoverBanner = useShouldShowBanner({ location: takeoverBannerLocation })
  const { currentPath, distinctId, token } = useLabRouteRequestContext()

  // LuxPlus region mismatch detection
  const isOutsideMemberRegion = useAppSelector(getIsLuxPlusMemberOutsideRegion)
  const memberRegion = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item?.region?.toLowerCase())

  const [, handleSwitchRegion] = useSwitchRegion(memberRegion)

  const switchRegionLabel = intl.formatMessage({
    id: 'U6MqSsCn0iaIY3J9jPvf',
    defaultMessage: 'Switch to home region',
    description: 'CTA label for LuxPlus header banner when member is browsing outside their membership region',
  })

  const luxPlusRegionOverride: LuxPlusRegionOverride | undefined = useMemo(() => {
    if (!isOutsideMemberRegion || !memberRegion) return undefined
    return {
      isOutsideMemberRegion: true,
      memberRegion,
      switchRegionLabel,
    }
  }, [isOutsideMemberRegion, memberRegion, switchRegionLabel])

  useEffect(() => {
    if (shouldShowTakeoverBanner) return
    dispatch(fetchLabHeaderBanners(currentPath, distinctId, token))
  }, [dispatch, currentPath, distinctId, token, shouldShowTakeoverBanner])

  const [dismissedBannerIds, setDismissedBannerIds] = useState<Set<string>>(() => getDismissedBannerIds())

  const refreshDismissedBannerIds = useCallback(() => {
    setDismissedBannerIds(getDismissedBannerIds())
  }, [])

  const headerBannersToRender = useMemo(() => {
    if (headerBannersLoadedPath !== currentPath) {
      return []
    }
    if (headerBannersRequestPath && headerBannersRequestPath !== currentPath) {
      return []
    }
    return getRenderableHeaderBanners(headerBanners, dismissedBannerIds)
  }, [headerBanners, dismissedBannerIds, headerBannersRequestPath, headerBannersLoadedPath, currentPath])

  const banner = useMemo(() => {
    if (campaign?.enabled && isBannerMessagePayload(campaign.value) && !dismissedBannerIds.has(campaign.value.key)) {
      return campaign.value
    }
    return undefined
  }, [campaign, dismissedBannerIds])

  useEffect(() => {
    if (!banner && headerBannersToRender.length === 0) {
      return
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshDismissedBannerIds()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [banner, headerBannersToRender.length, refreshDismissedBannerIds])

  const showModal = useContext(ModalContext)

  const openBannerModal = useCallback(() => {
    if (banner) {
      showModal(<NotificationBannerModal title={banner.modalTitle} content={banner.modalContent} />)
    }
  }, [banner, showModal])

  const dismissBanner = useCallback((bannerId: string) => {
    recordSiteBannerDismissal(bannerId, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
    refreshDismissedBannerIds()
  }, [refreshDismissedBannerIds])

  const dismissLegacyBanner = useCallback(() => {
    if (banner) {
      dismissBanner(banner.key)
    }
  }, [banner, dismissBanner])

  return useMemo(() => {
    if (shouldShowTakeoverBanner || isHidden) return []

    const items = headerBannersToRender.map((headerBanner): PageBannerCarouselItem => {
      const { kind, isCustomAppearance, description, cta, isLuxPlusRegionSwitch, background, textColor, ctaPrimaryTextColor, iconImageId } = getHeaderBannerPresentation(headerBanner, isMobile, luxPlusRegionOverride)
      const iconImageUrl = iconImageId
        ? getImageUrl(iconImageId, { height: 88, quality: 'good' })
        : undefined
      const isInlineCta = headerBanner.bannerId === INLINE_CTA_HEADER_BANNER_ID

      let backgroundCss: string | undefined
      let customBackground: React.ReactNode | undefined
      if (isCustomAppearance) {
        if (background.mode === 'image') {
          backgroundCss = DEFAULT_HEADER_BANNER_BACKGROUND
          customBackground = <HeaderBannerImageBackground $imageUrl={getSimpleImageUrl(background.imageId)} />
        } else if (background.mode === 'gradient') {
          backgroundCss = `${DEFAULT_HEADER_BANNER_BACKGROUND} ${background.value}`
        } else {
          backgroundCss = background.value
        }
      }

      return {
        id: `notification-header-${headerBanner.id}`,
        kind,
        onDismiss: () => dismissBanner(headerBanner.id),
        icon: iconImageUrl ? <HeaderBannerLeadingIcon src={iconImageUrl} alt="" aria-hidden="true" /> : undefined,
        customBackground,
        children: <>
          {renderHeaderBannerRichText(description)}
          {cta && !isInlineCta && <> </>}
        </>,
        // Both desktop (inline per-slide button) and mobile (shared MobileControls bar) size their
        // CTA-button layout slot based on actionButtonProps being present — for the inline-CTA banner
        // we still render the button, just invisible/non-interactive, purely to reserve the same
        // space other slides' real buttons occupy on both breakpoints, since the visible CTA is the
        // inline textLinkProps instead.
        actionButtonProps: cta ? {
          kind: cta.style,
          size: 'small',
          href: isLuxPlusRegionSwitch ? cta.path : undefined,
          to: isLuxPlusRegionSwitch ? undefined : cta.path,
          onClick: isLuxPlusRegionSwitch ? handleSwitchRegion : undefined,
          children: cta.label,
          ...(isInlineCta ? {
            'aria-hidden': true,
            tabIndex: -1,
            style: { visibility: 'hidden', pointerEvents: 'none' },
          } : {}),
        } : undefined,
        textLinkProps: cta && isInlineCta ? {
          href: isLuxPlusRegionSwitch ? cta.path : undefined,
          to: isLuxPlusRegionSwitch ? undefined : cta.path,
          onClick: isLuxPlusRegionSwitch ? handleSwitchRegion : undefined,
          children: cta.label,
        } : undefined,
        style: isCustomAppearance ? {
          background: backgroundCss,
          color: textColor,
          [BANNER_BUTTON_TEXT_COLOR_VAR]: textColor,
          [BANNER_BUTTON_PRIMARY_CONTRAST_COLOR_VAR]: ctaPrimaryTextColor,
        } : undefined,
      }
    })

    if (!banner) {
      return items
    }

    return [
      ...items,
      {
        id: `notification-campaign-${banner.key}`,
        kind: getBannerKind(banner.bannerType),
        icon: getBannerIcon(banner.bannerType),
        onDismiss: dismissLegacyBanner,
        children: <><b>{banner.boldText}</b> {banner.bannerText}</>,
        actionButtonProps: banner.modalContent ? {
          kind: 'primary' as const,
          onClick: openBannerModal,
          children: intl.formatMessage({ id: 'qimyJfMsL0jByto0bvax', defaultMessage: 'Read more', description: 'Button text to open modal with more details dialog window' }),
        } : undefined,
      },
    ]
  }, [shouldShowTakeoverBanner, isHidden, headerBannersToRender, banner, isMobile, dismissBanner, dismissLegacyBanner, openBannerModal, intl, luxPlusRegionOverride, handleSwitchRegion])
}

export default useNotificationBannerItems
